// InvoiceCoverageBanner (P1-2, 2026-05-09)
// 25日払い対象スパン (前月21日 〜 今月31日) の untouched 件数 / 推定金額 を 常時表示。
// untouched_5_25_due_estimate > 0 → 黄色 banner、 ≥10件 or ≥¥1M → 赤 banner。
// click で 「請求書処理」 page (= JournalVerificationPage) にジャンプ。

import React, { useEffect, useState } from 'react';
import { Button, Note, Text } from '@freee_jp/vibes';
import { MdWarning, MdInfo, MdError } from 'react-icons/md';

const API_BASE = 'https://invoice-review.system-0f7.workers.dev';

interface BulkActionCounts {
  eligible_for_bulk_override?: number;
  eligible_for_bulk_dispatch?: number;
  arica_direct_debit_candidates?: number;
  // Phase 5-D (2026-05-19): system-rejected retry stats
  system_rejected_total?: number;
  system_rejected_pending_retry?: number;
  user_rejected_total?: number;
}

interface CoverageSummary {
  receive_window: { from: string; to: string };
  total_received: number;
  by_status: Record<string, number>;
  untouched_total: number;
  untouched_5_25_due_estimate: number;
  likely_5_25_total_amount: number;
  bulk_action_counts?: BulkActionCounts;
}

interface BannerLevel {
  level: 'info' | 'warn' | 'error';
  bg: string;
  border: string;
  color: string;
  iconColor: string;
}

function bannerLevel(s: CoverageSummary): BannerLevel | null {
  const untouched = s.untouched_5_25_due_estimate || 0;
  const amount = s.likely_5_25_total_amount || 0;
  if (untouched === 0) return null;
  if (untouched >= 10 || amount >= 1_000_000) {
    return {
      level: 'error',
      bg: '#fdecec',
      border: '#f5a3a3',
      color: '#c33939',
      iconColor: '#c33939',
    };
  }
  return {
    level: 'warn',
    bg: '#fff4e5',
    border: '#ffd9a3',
    color: '#8c5a16',
    iconColor: '#c67320',
  };
}

function fmtYen(n: number): string {
  if (!n) return '¥0';
  return '¥' + Math.round(n).toLocaleString();
}

// 前月21日 を YYYY-MM-DD で
function defaultFromDate(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const fromY = m === 0 ? y - 1 : y;
  const fromM = m === 0 ? 12 : m;
  return `${fromY}-${String(fromM).padStart(2, '0')}-21`;
}

export function InvoiceCoverageBanner() {
  const [summary, setSummary] = useState<CoverageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Phase 5-D (2026-05-19): system-rejected retry banner 用 rids cache
  const [systemRejectedRids, setSystemRejectedRids] = useState<number[]>([]);
  const [retryDispatching, setRetryDispatching] = useState(false);
  const [retryResult, setRetryResult] = useState<string | null>(null);

  const fetchCoverage = async (cancelledRef?: { v: boolean }) => {
    try {
      const from = defaultFromDate();
      const url = `${API_BASE}/api/admin/invoice-coverage-audit?from=${from}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`coverage audit failed: ${r.status}`);
      const j = await r.json();
      if (!cancelledRef || !cancelledRef.v) {
        setSummary(j.summary || null);
        // Phase 5-D: rejected_system かつ eligible_for_bulk_dispatch=true の rids 抽出
        const rids: number[] = [];
        for (const row of (j.rows || [])) {
          if (row.current_status === 'rejected_system' && row.bulk_action_eligibility?.eligible_for_bulk_dispatch) {
            rids.push(row.rowid);
          }
        }
        setSystemRejectedRids(rids);
        setError(null);
      }
    } catch (e: any) {
      if (!cancelledRef || !cancelledRef.v) setError(e?.message || String(e));
    }
  };

  useEffect(() => {
    const cancelledRef = { v: false };
    (async () => {
      setLoading(true);
      await fetchCoverage(cancelledRef);
      if (!cancelledRef.v) setLoading(false);
    })();
    return () => { cancelledRef.v = true; };
  }, []);

  // 5 分 ごとに 自動 refresh (= cron tick 直後にも UI が反映される)
  useEffect(() => {
    const interval = setInterval(() => { fetchCoverage(); }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Phase 5-D: 「system reject 自動 retry」 click handler
  const onRetrySystemRejected = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (retryDispatching || systemRejectedRids.length === 0) return;
    if (!window.confirm(`system reject ${systemRejectedRids.length} 件を fresh vision で retry します。 よろしいですか?`)) return;
    setRetryDispatching(true);
    setRetryResult(null);
    try {
      const r = await fetch(`${API_BASE}/api/admin/invoice-bulk-dispatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rowids: systemRejectedRids,
          flow_type: '25th_payment',
          force_fresh_vision: true,
          dry_run: false,
          session_id: 'web_app_user_phase5d',
        }),
      });
      const j = await r.json();
      if (r.ok && j.ok) {
        setRetryResult(`retry batch=${j.batch_id || 'n/a'} eligible=${j.total ?? 0}`);
        // 再取得
        await fetchCoverage();
      } else {
        setRetryResult(`error: ${j.error || j.hint || `status ${r.status}`}`);
      }
    } catch (err: any) {
      setRetryResult(`fetch error: ${err?.message || String(err)}`);
    } finally {
      setRetryDispatching(false);
    }
  };

  if (loading && !summary) {
    return null; // 初回 load 中は表示なし (= flicker 回避)
  }

  if (error) {
    // 取得 error も無視 — banner は冗長なので UI 邪魔にならない
    return null;
  }

  if (!summary) return null;

  const systemRejectedPendingRetry = summary.bulk_action_counts?.system_rejected_pending_retry || 0;
  const level = bannerLevel(summary);

  // Phase 5-D: untouched=0 でも system_rejected_pending_retry>0 なら banner 表示
  if (!level && systemRejectedPendingRetry === 0) {
    return null;
  }

  const untouched = summary.untouched_5_25_due_estimate;
  const amount = summary.likely_5_25_total_amount || 0;
  const untouchedTotal = summary.untouched_total || 0;
  // level が null (untouched=0) でも system_rejected ありなら warn level で表示
  const effectiveLevel: BannerLevel = level || {
    level: 'warn',
    bg: '#fff4e5',
    border: '#ffd9a3',
    color: '#8c5a16',
    iconColor: '#c67320',
  };
  const Icon = effectiveLevel.level === 'error' ? MdError : MdWarning;

  const onClick = () => {
    // 既に invoice page にいるなら scroll-to-top のみ。 他 page なら hash 切替
    if (window.location.hash !== '#invoice') {
      window.location.hash = '#invoice';
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      style={{
        backgroundColor: effectiveLevel.bg,
        borderLeft: `4px solid ${effectiveLevel.iconColor}`,
        border: `1px solid ${effectiveLevel.border}`,
        borderLeftWidth: '4px',
        borderRadius: '4px',
        padding: '12px 16px',
        margin: '12px 16px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
      }}
    >
      <Icon size={24} color={effectiveLevel.iconColor} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {level ? (
          <div style={{ fontWeight: 600, color: effectiveLevel.color, fontSize: '14px' }}>
            {effectiveLevel.level === 'error' ? '【高優先】' : '【対応待ち】'}
            25日払い対象 未処理 {untouched} 件 / 推定 {fmtYen(amount)}
          </div>
        ) : null}
        {systemRejectedPendingRetry > 0 ? (
          <div style={{ fontWeight: 600, color: effectiveLevel.color, fontSize: '14px', marginTop: level ? '4px' : 0 }}>
            system reject (= 自動 retry 待機) {systemRejectedPendingRetry} 件 / force_fresh_vision で再走可
          </div>
        ) : null}
        <div style={{ fontSize: '12px', color: effectiveLevel.color, opacity: 0.8, marginTop: '2px' }}>
          受信 {summary.total_received} 件 (期間 {summary.receive_window.from} 〜 {summary.receive_window.to || '今'}) / 全体 untouched {untouchedTotal} 件 / 詳細は GET /api/admin/invoice-coverage-audit
          {' '}/ 「請求書処理」 page で承認/却下/登録してください
        </div>
        {retryResult ? (
          <div style={{ fontSize: '11px', color: effectiveLevel.color, opacity: 0.9, marginTop: '4px', fontFamily: 'monospace' }}>
            {retryResult}
          </div>
        ) : null}
      </div>
      {systemRejectedPendingRetry > 0 ? (
        <Button
          appearance="primary"
          small
          onClick={onRetrySystemRejected}
          disabled={retryDispatching || systemRejectedRids.length === 0}
        >
          {retryDispatching ? 'retry 投入中…' : `system reject を retry (${systemRejectedRids.length})`}
        </Button>
      ) : null}
      <Button appearance="secondary" small>
        詳細を確認
      </Button>
    </div>
  );
}
